import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { marked } from "marked";
import { blogPosts } from "data/blogPosts";
import { useTranslation } from "lib/i18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStop, faVolumeHigh } from "@fortawesome/free-solid-svg-icons";
import Seo from "components/Seo";
import { getAbsoluteUrl, getBlogPostingSchema } from "lib/seo";

const getLocalizedValue = (value, language) => {
  if (value && typeof value === "object") {
    return value[language] || value.en || Object.values(value)[0];
  }
  return value;
};

const BlogPost = () => {
  const { slug } = useParams();
  const { t, language } = useTranslation();
  const post = useMemo(() => blogPosts.find((entry) => entry.slug === slug), [slug]);
  const [contentHtml, setContentHtml] = useState("");
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [isReading, setIsReading] = useState(false);
  const [readAloudError, setReadAloudError] = useState("");
  const utteranceRef = useRef(null);
  const supportsSpeech = typeof window !== "undefined" && "speechSynthesis" in window;

  useEffect(() => {
    if (!post) return;
    const contentPath =
      language === "fr" && post.contentFr ? post.contentFr : post.content;
    const contentUrl = `${process.env.PUBLIC_URL}${contentPath}`;

    setStatus("loading");
    fetch(contentUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Unable to load post content");
        }
        return response.text();
      })
      .then((text) => {
        if (post.format === "markdown") {
          setContentHtml(marked.parse(text));
        } else {
          setContentHtml(text);
        }
        setStatus("ready");
      })
      .catch(() => {
        setContentHtml("");
        setStatus("error");
      });
  }, [post, language]);

  useEffect(() => {
    if (status === "ready") {
      setReadAloudError("");
    }
  }, [status]);

  useEffect(() => {
    const updateProgress = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) {
        setProgress(0);
        return;
      }
      const percent = (window.scrollY / scrollable) * 100;
      setProgress(Math.max(0, Math.min(100, Math.round(percent))));
    };

    updateProgress();
    window.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress);

    return () => {
      window.removeEventListener("scroll", updateProgress);
      window.removeEventListener("resize", updateProgress);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (supportsSpeech) {
        window.speechSynthesis.cancel();
      }
    };
  }, [supportsSpeech]);

  useEffect(() => {
    if (!supportsSpeech) return;
    window.speechSynthesis.cancel();
    setIsReading(false);
  }, [language, slug, supportsSpeech]);

  if (!post) {
    return (
      <>
        <Seo
          title={t("blog.notFound")}
          description={t("blog.loadError")}
          path={`/blog/${slug}`}
          noIndex
        />
        <section className="blog section-title mt-5">
          <div className="container text-white">
            <div className="blog-post">
              <h1 className="title-font">{t("blog.notFound")}</h1>
              <Link className="blog-card-link" to="/blog">
                {t("blog.backToBlog")}
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  const title = getLocalizedValue(post.title, language);
  const summary = getLocalizedValue(post.summary, language);
  const category = getLocalizedValue(post.category, language);
  const path = `/blog/${post.slug}`;
  const schema = getBlogPostingSchema({
    title,
    description: summary,
    date: post.date,
    url: getAbsoluteUrl(path),
  });
  const canReadAloud = supportsSpeech && status === "ready" && !readAloudError;

  const getReadableText = () => {
    if (!contentHtml) return "";
    const parser = new DOMParser();
    const doc = parser.parseFromString(contentHtml, "text/html");
    return doc.body.textContent || "";
  };

  const handleReadAloud = () => {
    if (!supportsSpeech) {
      setReadAloudError(t("blog.readAloudUnavailable"));
      return;
    }
    const text = getReadableText().trim();
    if (!text) {
      setReadAloudError(t("blog.loadError"));
      return;
    }
    setReadAloudError("");
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language === "fr" ? "fr-CA" : "en-CA";
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find((voice) =>
      voice.lang.toLowerCase().startsWith(language)
    );
    if (preferred) {
      utterance.voice = preferred;
    }
    utterance.onend = () => setIsReading(false);
    utterance.onerror = () => setIsReading(false);
    utteranceRef.current = utterance;
    setIsReading(true);
    window.speechSynthesis.speak(utterance);
  };

  const handleStopReading = () => {
    if (!supportsSpeech) return;
    window.speechSynthesis.cancel();
    setIsReading(false);
  };

  return (
    <>
      <Seo
        title={title}
        description={summary}
        path={path}
        type="article"
        schema={[schema]}
      />
      <section className="blog section-title mt-5">
        <div className="container text-white">
          <div
            className="reading-progress"
            role="progressbar"
            aria-label={t("blog.readingProgress")}
            aria-valuenow={progress}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <span className="reading-progress-bar" style={{ width: `${progress}%` }} />
          </div>
          <div className="blog-post">
            <div className="blog-card-meta title-font-2">
              <span className="blog-category">{category}</span>
              <span className="blog-date">{post.date}</span>
            </div>
            <h1 className="title-font blog-post-title">{title}</h1>
            <div className="blog-post-actions">
              <Link className="blog-card-link" to="/blog">
                {t("blog.backToBlog")}
              </Link>
              <button
                type="button"
                className="btn cta-accent read-aloud-button"
                onClick={isReading ? handleStopReading : handleReadAloud}
                aria-pressed={isReading}
                aria-label={isReading ? t("blog.stopReading") : t("blog.readAloud")}
                title={isReading ? t("blog.stopReading") : t("blog.readAloud")}
                disabled={!canReadAloud}
              >
                <FontAwesomeIcon icon={isReading ? faStop : faVolumeHigh} aria-hidden="true" />
              </button>
            </div>
            {!supportsSpeech && (
              <p className="title-font-2 read-aloud-status">{t("blog.readAloudUnavailable")}</p>
            )}
            {readAloudError && (
              <p className="title-font-2 read-aloud-status">{readAloudError}</p>
            )}
            {status === "loading" && (
              <p className="title-font-2">{t("blog.loading")}</p>
            )}
            {status === "error" && (
              <p className="title-font-2">{t("blog.loadError")}</p>
            )}
            {status === "ready" && (
              <div
                className="blog-content title-font-2"
                // Content is sourced from local files. Keep trusted content only.
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogPost;
